import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import { HttpStatusCode, type AxiosError } from 'axios';
import {
  OtpRateLimitStateReasonEnum,
  VerifyOtpIncorrectResponseReasonEnum,
  type OtpRateLimitState,
  type VerifyOtpIncorrectResponse,
  type CognitoSignInSuccessResponse,
} from '@bvengineering/cas-member-gateway-service-client';
import { useToast } from '@/primer/components';
import { useInitiateOtp, usePostAuthenticate, useVerifyOtp, type InitiateOtpResponse } from '@/api';
import { useAuth } from '@/api/hooks/useAuth';
import { AccountLockout, InviteErrorScreen, InviteErrorVariant } from '@/components/ui';
import { PageLayout } from '@/components/layout';
import { getBlockedDeadline } from '@/utils/inviteLockout';
import {
  classifyPostAuthenticateError,
  clearOtpChallenge,
  getSafeRedirect,
  InviteAuthFailure,
  readOtpAttemptsRemaining,
  readOtpChallenge,
  readOtpEmail,
  storeOtpChallenge,
  MS_PER_SECOND,
  OTP_MAX_ATTEMPTS,
  OTP_RESEND_COOLDOWN_MS,
  OTP_STATE,
  OTP_TOAST,
  type OtpState,
} from '@/utils';
import { InviteOtpView } from './InviteOtpView';

const STORAGE_KEY = 'signin';

type OtpFailure = VerifyOtpIncorrectResponse | OtpRateLimitState | { reason?: undefined };

function readFailure(error: unknown) {
  const response = (error as AxiosError<OtpFailure>)?.response;
  return { status: response?.status, failure: response?.data };
}

function isBlockedRateLimit(body: OtpFailure | undefined): body is OtpRateLimitState {
  return body?.reason === OtpRateLimitStateReasonEnum.Blocked;
}

export function SignInOtp() {
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: '/_public/signin/otp' });
  const safeRedirect = getSafeRedirect(redirect);
  const { showToast } = useToast();
  const { completeOtpSignIn, isAuthenticated } = useAuth();

  const verifyOtp = useVerifyOtp();
  const postAuthenticate = usePostAuthenticate();
  const initiateOtp = useInitiateOtp();

  const [challenge, setChallenge] = useState(() => readOtpChallenge(STORAGE_KEY));
  const [email] = useState(() => readOtpEmail(STORAGE_KEY));
  const [attemptsRemaining, setAttemptsRemaining] = useState(() =>
    readOtpAttemptsRemaining(STORAGE_KEY)
  );
  const [state, setState] = useState<OtpState>(() =>
    attemptsRemaining === 0 ? OTP_STATE.ATTEMPTS_EXHAUSTED : OTP_STATE.ENTRY
  );

  const [resendExpiresAt, setResendExpiresAt] = useState(() => Date.now() + OTP_RESEND_COOLDOWN_MS);
  const [retryExpiresAt, setRetryExpiresAt] = useState(0);
  const [inputResetKey, setInputResetKey] = useState(0);
  const resetInput = () => setInputResetKey((key) => key + 1);

  const [destination, setDestination] = useState<string | null>(null);
  const [authFailure, setAuthFailure] = useState<InviteAuthFailure | null>(null);
  const [lockoutExpiresAt, setLockoutExpiresAt] = useState(0);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Navigate once the auth context has settled.
  useEffect(() => {
    if (destination && isAuthenticated) void navigate({ to: destination, replace: true });
  }, [destination, isAuthenticated, navigate]);

  const lockOut = (expiresAt: number) => {
    clearOtpChallenge();
    setLockoutExpiresAt(expiresAt);
    setState(OTP_STATE.BLOCKED);
  };

  const rejectIncorrectCode = (replacement: InitiateOtpResponse) => {
    const next = Math.max(0, attemptsRemaining - 1);
    setChallenge(replacement);
    storeOtpChallenge(STORAGE_KEY, replacement, { email, attemptsRemaining: next });
    setAttemptsRemaining(next);
    setState(next ? OTP_STATE.INCORRECT : OTP_STATE.ATTEMPTS_EXHAUSTED);
    resetInput();
    showToast(OTP_TOAST.INCORRECT_CODE);
  };

  // Cognito dropped the challenge session, so the user starts over from the email step.
  const restartFromEmail = () => {
    clearOtpChallenge();
    setChallenge(null);
  };

  const handleVerifyFailure = (error: unknown) => {
    const { status, failure } = readFailure(error);

    if (status === HttpStatusCode.TooManyRequests && isBlockedRateLimit(failure)) {
      try {
        lockOut(getBlockedDeadline(HttpStatusCode.TooManyRequests, failure) ?? Date.now());
      } catch {
        setState(OTP_STATE.ERROR);
        resetInput();
      }
    } else if (
      status === HttpStatusCode.BadRequest &&
      failure?.reason === VerifyOtpIncorrectResponseReasonEnum.IncorrectCode
    ) {
      rejectIncorrectCode({ session: failure.session, challengeName: failure.challengeName });
    } else if (
      status === HttpStatusCode.TooManyRequests ||
      (status === HttpStatusCode.BadRequest && attemptsRemaining <= 1)
    ) {
      // Non-BLOCKED 429 (MAX_LIMITS_REACHED) or final-attempt 400 — per-code budget exhausted.
      setAttemptsRemaining(0);
      if (challenge) storeOtpChallenge(STORAGE_KEY, challenge, { email, attemptsRemaining: 0 });
      setState(OTP_STATE.ATTEMPTS_EXHAUSTED);
      resetInput();
    } else if (status === HttpStatusCode.BadRequest) {
      // Session error with attempts remaining — restart from email.
      restartFromEmail();
    } else {
      setState(OTP_STATE.ERROR);
      resetInput();
    }
  };

  const handleResendFailure = (error: unknown, previousState: OtpState) => {
    const { status, failure } = readFailure(error);
    const rateLimit = status === HttpStatusCode.TooManyRequests ? failure : undefined;

    switch (rateLimit?.reason) {
      case OtpRateLimitStateReasonEnum.Blocked:
        try {
          lockOut(getBlockedDeadline(HttpStatusCode.TooManyRequests, rateLimit) ?? Date.now());
        } catch {
          setState(previousState);
          showToast(OTP_TOAST.REQUEST_FAILED);
        }
        break;
      case OtpRateLimitStateReasonEnum.SendLimited:
        setRetryExpiresAt(Date.now() + rateLimit.retryAfterSeconds * MS_PER_SECOND);
        setState(OTP_STATE.SEND_LIMITED);
        showToast(OTP_TOAST.TOO_MANY_REQUESTS);
        break;
      case OtpRateLimitStateReasonEnum.Cooldown: {
        const expiresAt = Date.now() + rateLimit.retryAfterSeconds * MS_PER_SECOND;
        setResendExpiresAt(expiresAt);
        if (previousState === OTP_STATE.SEND_LIMITED) setRetryExpiresAt(expiresAt);
        setState(previousState);
        break;
      }
      default:
        setState(previousState);
        showToast(OTP_TOAST.REQUEST_FAILED);
    }
  };

  const handleAuthFailure = (failure: InviteAuthFailure) => {
    showToast(OTP_TOAST.REQUEST_FAILED);
    setAuthFailure(failure);
  };

  const handleComplete = async (code: string) => {
    if (!challenge || verifyOtp.isPending || state === OTP_STATE.BLOCKED) return;

    setState(OTP_STATE.VERIFYING);
    let tokens: CognitoSignInSuccessResponse;
    try {
      tokens = await verifyOtp.mutateAsync({
        request: { session: challenge.session, code, email },
      });
    } catch (error) {
      if (!mountedRef.current) return;
      handleVerifyFailure(error);
      return;
    }

    if (!mountedRef.current) return;
    clearOtpChallenge();

    try {
      await postAuthenticate.mutateAsync({ accessToken: tokens.accessToken });
      await completeOtpSignIn(tokens);
    } catch (error) {
      if (!mountedRef.current) return;
      console.error(
        'OTP authentication failed after verify:',
        error instanceof Error ? error.message : error
      );
      handleAuthFailure(classifyPostAuthenticateError(error));
      return;
    }

    if (!mountedRef.current) return;
    setDestination(safeRedirect ?? '/dashboard');
  };

  const handleResend = async () => {
    const isCoolingDown = Date.now() < resendExpiresAt || Date.now() < retryExpiresAt;
    if (
      initiateOtp.isPending ||
      verifyOtp.isPending ||
      state === OTP_STATE.BLOCKED ||
      isCoolingDown
    )
      return;

    const previousState = state;
    setState(OTP_STATE.VERIFYING);

    try {
      const nextChallenge = await initiateOtp.mutateAsync({ email });
      if (!mountedRef.current) return;
      setChallenge(nextChallenge);
      storeOtpChallenge(STORAGE_KEY, nextChallenge, { email });
      setAttemptsRemaining(OTP_MAX_ATTEMPTS);
      setResendExpiresAt(Date.now() + OTP_RESEND_COOLDOWN_MS);
      setState(OTP_STATE.ENTRY);
      resetInput();
      showToast(OTP_TOAST.CODE_SENT);
    } catch (error) {
      if (!mountedRef.current) return;
      handleResendFailure(error, previousState);
    }
  };

  if (state === OTP_STATE.BLOCKED) {
    return (
      <PageLayout className="py-8" gridProps={{ className: 'px-5' }}>
        <AccountLockout
          expiresAt={lockoutExpiresAt}
          onExpire={() =>
            void navigate({
              to: '/signin/email',
              ...(safeRedirect && { search: { redirect: safeRedirect } }),
              replace: true,
            })
          }
        />
      </PageLayout>
    );
  }

  if (authFailure === InviteAuthFailure.CONFLICT) {
    return <InviteErrorScreen variant={InviteErrorVariant.SIGN_IN_FAILED} />;
  }

  // OTP session is consumed — creator must restart from the email step.
  if (authFailure) {
    return (
      <InviteErrorScreen
        variant={InviteErrorVariant.BAD_REQUEST}
        onBack={() =>
          void navigate({
            to: '/signin/email',
            ...(safeRedirect && { search: { redirect: safeRedirect } }),
            replace: true,
          })
        }
      />
    );
  }

  if (!challenge) {
    return (
      <Navigate
        to="/signin/email"
        search={safeRedirect ? { redirect: safeRedirect } : undefined}
        replace
      />
    );
  }

  return (
    <InviteOtpView
      state={state}
      attemptsRemaining={attemptsRemaining}
      resendExpiresAt={resendExpiresAt}
      retryExpiresAt={retryExpiresAt}
      inputResetKey={inputResetKey}
      onComplete={(code) => void handleComplete(code)}
      onResend={() => void handleResend()}
    />
  );
}

export default SignInOtp;
